// Content-free, best-effort render receipts. No model call, artifact prefetch,
// automatic chart selection, or awaited request is introduced by observation.
const traces = new Map();
const earlyEvents = [];
const MAX_AGE = 24 * 60 * 60 * 1000;
export const WORKFLOW_ANSWER_VISIBLE = "incite:workflow-answer-visible";
export const WORKFLOW_FILE_READY = "incite:workflow-file-ready";

export const startWorkflowDelivery = () => ({
  started: performance.now(), timeOrigin: performance.timeOrigin,
});
export const dispatchWorkflowDelivery = (submission) => {
  if (!submission || submission.timeOrigin !== performance.timeOrigin) return null;
  return { ...submission, queue_wait_ms: Math.max(0, performance.now() - submission.started) };
};

export const applyWorkflowDeliveryEvent = (trace, kind, id, elapsed) => {
  if (!Number.isFinite(elapsed) || elapsed < 0 || elapsed > MAX_AGE) return false;
  if (kind === "answer") {
    if (String(id) !== String(trace.receipt.response_message_id) || trace.receipt.answer_visible_ms != null) return false;
    trace.receipt.answer_visible_ms = elapsed;
  } else {
    const key = String(id);
    if (!trace.manifest[kind]?.map(String).includes(key) || trace.receipt[kind][key] != null) return false;
    trace.receipt[kind][key] = elapsed;
  }
  return true;
};

const send = (trace) => {
  trace.receipt.sequence += 1;
  // A failed send leaves coverage unknown. Later milestones send the cumulative
  // receipt, so losing an intermediate request cannot erase an earlier milestone.
  fetch("/graphql", {
    method: "POST", credentials: "same-origin", keepalive: true,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ query: "mutation WorkflowDelivery($receipt: JSON!) { recordWorkflowDelivery(receipt: $receipt) }", variables: { receipt: trace.receipt } }),
  }).catch(() => {});
};

export const bindWorkflowDelivery = (submission, observation) => {
  if (!submission || submission.timeOrigin !== performance.timeOrigin || !observation?.attempt_id) return;
  const now = performance.now();
  for (const [key, trace] of traces) if (now - trace.started > MAX_AGE) traces.delete(key);
  // Bound telemetry memory only; no conversation/context data is retained here.
  if (traces.size >= 100) traces.delete(traces.keys().next().value);
  const trace = {
    started: submission.started, manifest: observation.delivery_manifest || {},
    receipt: {
      attempt_id: observation.attempt_id, submitted_request_id: observation.submitted_request_id,
      response_message_id: observation.response_message_id, sequence: 0,
      queue_wait_ms: submission.queue_wait_ms, answer_visible_ms: null, charts: {}, files: {},
    },
  };
  traces.set(observation.attempt_id, trace);
  // A streamed answer/history hydration may paint before its HTTP handshake.
  // Replay only exact identities observed after this submission began.
  for (const event of earlyEvents) {
    if (event.answerId != null && String(event.answerId) !== String(trace.receipt.response_message_id)) continue;
    if (event.at >= submission.started + (submission.queue_wait_ms || 0)) {
      applyWorkflowDeliveryEvent(trace, event.kind, event.id, event.at - submission.started);
    }
  }
  send(trace);
};

const observe = (kind, id, answerId = null) => {
  if (document.visibilityState !== "visible") return;
  const at = performance.now();
  earlyEvents.push({ kind, id, answerId, at });
  while (earlyEvents.length > 1000 || (earlyEvents[0] && at - earlyEvents[0].at > MAX_AGE)) earlyEvents.shift();
  for (const trace of traces.values()) {
    if (answerId != null && String(answerId) !== String(trace.receipt.response_message_id)) continue;
    if (applyWorkflowDeliveryEvent(trace, kind, id, at - trace.started)) send(trace);
  }
};
if (typeof window !== "undefined" && typeof window.addEventListener === "function") {
  window.addEventListener(WORKFLOW_ANSWER_VISIBLE, (event) => observe("answer", event.detail.messageId));
  window.addEventListener(WORKFLOW_FILE_READY, (event) => observe("files", event.detail.attachmentId));
  window.addEventListener("incite:workspace-chart-delivery", (event) => {
    if (event.detail.status === "rendered") observe("charts", event.detail.ticker_id, event.detail.answer_message_id);
  });
}
